import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, {
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  InvoiceItemInputGQLType,
  LoanRecordFragmentGQLType,
  TAccountGQLType,
  TAccountTypeGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { AccrualsByTAccountMap, getCapitalizedLoanLabel } from '../utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { SearchSelect } from 'packages/common-frontend/components/SearchSelect';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  dollarNumberWithCommas,
  getTAccountTypeLabel,
} from 'packages/common-frontend/utils';
import { CurrencyInput } from 'packages/funding-requests/ui/submit/CreateRequest/CurrencyInput';
import { useCreateInvoiceContext } from 'packages/invoices/hooks/useCreateInvoice';
import { useGetLoanRecordsByMetadataQuery } from 'packages/loans/requests/loans.gql.generated';

const LineItemOptionDropdown = ({
  lineItemIndex,
  accrualsByTAccountMap,
  invoiceComponentIndex,
}: {
  lineItemIndex: number;
  accrualsByTAccountMap: AccrualsByTAccountMap;
  invoiceComponentIndex: number;
}) => {
  const {
    invoiceComponentsWithInvoiceItems,
    updateInvoiceItemsAndTotalsForInvoiceComponent,
  } = useCreateInvoiceContext();

  const invoiceItems =
    invoiceComponentsWithInvoiceItems[invoiceComponentIndex].invoiceItems;

  const options = useMemo(() => {
    const allUsableAccountingAccountIds = Object.keys(
      accrualsByTAccountMap,
    ) as TAccountTypeGQLType[];
    const allUsedAccountingAccountIds = invoiceItems.map(
      (invoiceItem) => invoiceItem.accountingAccountId,
    );
    const unusedAccountingAccountIds = allUsableAccountingAccountIds.filter(
      (accountingAccountId) =>
        !allUsedAccountingAccountIds.includes(accountingAccountId),
    );

    const currentSelectedAccountingAccountIdForRow =
      invoiceItems[lineItemIndex].accountingAccountId;

    // include the current selected account in the list of options, and any other unused
    return [
      ...unusedAccountingAccountIds,
      currentSelectedAccountingAccountIdForRow,
    ].map((accountingAccountId) => {
      const tAccount =
        accrualsByTAccountMap[accountingAccountId]?.tAccount.tAccount;
      return {
        label: getTAccountTypeLabel(tAccount),
        value: accountingAccountId,
      };
    });
  }, [accrualsByTAccountMap, invoiceItems, lineItemIndex]);

  return (
    <SearchSelect
      options={options}
      value={options.find(
        (option) =>
          option.value === invoiceItems[lineItemIndex].accountingAccountId,
      )}
      onChange={(newAccountingAccountId) => {
        const newInvoiceItems = _.cloneDeep(invoiceItems);
        const currentInvoiceItem = newInvoiceItems[lineItemIndex];

        const newAccountingAccount =
          accrualsByTAccountMap[newAccountingAccountId].tAccount;
        const {
          id,
          tAccount,
          organizationId,
          creditFacilityId,
        } = newAccountingAccount;

        // no need to attach finley accrued amount for principal
        // only applicable to tAccount types that actually accrue
        const newInvoiceItemFinleyAccruedAmount =
          tAccount === 'longTermDebt'
            ? null
            : accrualsByTAccountMap[newAccountingAccountId]?.totalAccruedAmount;

        const newInvoiceItem: InvoiceItemInputGQLType = {
          ...currentInvoiceItem,
          accountingAccountId: newAccountingAccountId,
          accountingAccount: {
            id,
            tAccount,
            organizationId,
            creditFacilityId,
          },
          amount:
            accrualsByTAccountMap[newAccountingAccountId]?.totalAccruedAmount ??
            0, // auto default to full accrued amount for selected period if it exists
          finleyAccruedAmount: newInvoiceItemFinleyAccruedAmount,
        };

        newInvoiceItems.splice(lineItemIndex, 1, newInvoiceItem);
        updateInvoiceItemsAndTotalsForInvoiceComponent({
          invoiceComponentIndex,
          newInvoiceItemsForInvoiceComponent: newInvoiceItems,
        });
      }}
      isSelectedColor={colors.gray[50]}
      isFocusedColor={colors.gray[50]}
      fontWeight={400}
      placeholder="Select type"
    />
  );
};

const CashInterestItem = ({
  invoiceItem,
  lineItemIndex,
  invoiceComponentIndex,
  inputRef,
}: {
  invoiceItem: InvoiceItemInputGQLType;
  lineItemIndex: number;
  invoiceComponentIndex: number;
  inputRef: RefObject<HTMLInputElement>;
}) => {
  const {
    invoiceComponentsWithInvoiceItems,
    updateInvoiceItemsAndTotalsForInvoiceComponent,
  } = useCreateInvoiceContext();

  return (
    <Box w="100%">
      <Box mb="8px">
        <Typography fontWeight="semibold">Cash</Typography>
      </Box>
      <CurrencyInput
        textAlign="left"
        state={invoiceItem.amount}
        updateState={(newInputAmount) => {
          const newInvoiceItems = _.cloneDeep(
            invoiceComponentsWithInvoiceItems[invoiceComponentIndex]
              .invoiceItems,
          );
          const currentInvoiceItem = newInvoiceItems[lineItemIndex];

          const newInvoiceItem: InvoiceItemInputGQLType = {
            ...currentInvoiceItem,
            amount: newInputAmount ?? 0,
          };

          newInvoiceItems.splice(lineItemIndex, 1, newInvoiceItem);

          updateInvoiceItemsAndTotalsForInvoiceComponent({
            invoiceComponentIndex,
            newInvoiceItemsForInvoiceComponent: newInvoiceItems,
          });
        }}
        inputRef={inputRef}
      />
    </Box>
  );
};

const PaymentInKindInterestItem = ({
  invoiceItem,
  lineItemIndex,
  invoiceComponentIndex,
  paymentInKindEligibleLoans,
  inputRef,
}: {
  invoiceItem: InvoiceItemInputGQLType;
  lineItemIndex: number;
  invoiceComponentIndex: number;
  paymentInKindEligibleLoans: LoanRecordFragmentGQLType[];
  inputRef: RefObject<HTMLInputElement>;
}) => {
  const {
    invoiceComponentsWithInvoiceItems,
    updateInvoiceItemsAndTotalsForInvoiceComponent,
  } = useCreateInvoiceContext();

  const invoiceComponentLoanId =
    invoiceComponentsWithInvoiceItems[invoiceComponentIndex].invoiceComponent
      .finleyLoanId;
  const invoiceItems =
    invoiceComponentsWithInvoiceItems[invoiceComponentIndex].invoiceItems;

  const paymentInKindLoanOptions: {
    label: string;
    value: string;
  }[] = useMemo(() => {
    const existingCapitalizedLoansOnInterestInvoiceItems = _.compact(
      invoiceItems.map(
        (existingInvoiceItem) =>
          existingInvoiceItem.accountingAccount.tAccount ===
            'accruedInterest' && existingInvoiceItem.capitalizedLoanId,
      ),
    );

    const unusedPaymentInKindLoans = _.compact(
      paymentInKindEligibleLoans.map((loan) => {
        if (existingCapitalizedLoansOnInterestInvoiceItems.includes(loan.id)) {
          return null;
        }

        return {
          label: getCapitalizedLoanLabel({
            selectedLoanId: invoiceComponentLoanId,
            loanName: loan.name,
            loanId: loan.id,
            externalLoanId: loan.externalLoanId,
          }),
          value: loan.id,
        };
      }),
    );

    const currentInvoiceItemCapitalizedLoan = paymentInKindEligibleLoans.find(
      (loan) => loan.id === invoiceItem.capitalizedLoanId,
    );

    const currentInvoiceItemCapitalizedLoanOptions = currentInvoiceItemCapitalizedLoan
      ? [
          {
            label:
              currentInvoiceItemCapitalizedLoan.id === invoiceComponentLoanId
                ? 'This Loan'
                : currentInvoiceItemCapitalizedLoan.name,
            value: currentInvoiceItemCapitalizedLoan.id,
          },
        ]
      : [];

    return [
      ...unusedPaymentInKindLoans,
      ...currentInvoiceItemCapitalizedLoanOptions,
    ];
  }, [
    paymentInKindEligibleLoans,
    invoiceItems,
    invoiceComponentLoanId,
    invoiceItem.capitalizedLoanId,
  ]);

  return (
    <Flex gap="16px" w="100%">
      <Box flexGrow={1}>
        <Box mb="8px">
          <Typography fontWeight="semibold">Interest Capitalization</Typography>
        </Box>
        <CurrencyInput
          textAlign="left"
          state={invoiceItem.amount}
          updateState={(newInputAmount) => {
            const newInvoiceItems = _.cloneDeep(invoiceItems);
            const currentInvoiceItem = newInvoiceItems[lineItemIndex];

            const newInvoiceItem: InvoiceItemInputGQLType = {
              ...currentInvoiceItem,
              amount: newInputAmount ?? 0,
            };

            newInvoiceItems.splice(lineItemIndex, 1, newInvoiceItem);

            updateInvoiceItemsAndTotalsForInvoiceComponent({
              invoiceComponentIndex,
              newInvoiceItemsForInvoiceComponent: newInvoiceItems,
            });
          }}
          inputRef={inputRef}
        />
      </Box>
      <Box flexGrow={1}>
        <Box mb="8px">
          <Typography fontWeight="semibold">Loan</Typography>
        </Box>
        <SearchSelect
          options={paymentInKindLoanOptions}
          value={paymentInKindLoanOptions.find(
            (loanOption) => loanOption.value === invoiceItem.capitalizedLoanId,
          )}
          onChange={(newLoanId) => {
            if (newLoanId) {
              const newInvoiceItems = _.cloneDeep(invoiceItems);
              const currentInvoiceItem = newInvoiceItems[lineItemIndex];

              const newInvoiceItem: InvoiceItemInputGQLType = {
                ...currentInvoiceItem,
                capitalizedLoanId: newLoanId,
              };

              newInvoiceItems.splice(lineItemIndex, 1, newInvoiceItem);
              updateInvoiceItemsAndTotalsForInvoiceComponent({
                invoiceComponentIndex,
                newInvoiceItemsForInvoiceComponent: newInvoiceItems,
              });
            }
          }}
          isSelectedColor={colors.gray[50]}
          isFocusedColor={colors.gray[50]}
          fontWeight={400}
          placeholder="Select type"
        />
      </Box>
    </Flex>
  );
};

const LineItemInterestInvoiceAmountCellContent = ({
  totalAccruedInterest,
  selectedCreditFacilityId,
  accrualsByTAccountMap,
  invoiceComponentIndex,
}: {
  totalAccruedInterest: number;
  selectedCreditFacilityId: string;
  accrualsByTAccountMap: AccrualsByTAccountMap;
  invoiceComponentIndex: number;
}) => {
  const {
    invoice,
    invoiceComponentsWithInvoiceItems,
    updateInvoiceItemsAndTotalsForInvoiceComponent,
    selectedEntities: {
      loan: { loan: selectedLoan },
    },
  } = useCreateInvoiceContext();

  const {
    data: loansSummaryData,
    loading: isLoansSummaryLoading,
  } = useGetLoanRecordsByMetadataQuery({
    variables: {
      filterParams: {
        creditFacilityIds: [selectedCreditFacilityId],
      },
    },
    fetchPolicy: 'network-only',
  });

  const invoiceItems =
    invoiceComponentsWithInvoiceItems[invoiceComponentIndex].invoiceItems;

  const onClickDeleteInterestItem: (
    lineItemIndex: number,
  ) => void = useCallback(
    (lineItemIndex: number) => {
      const newInvoiceItems = _.cloneDeep(invoiceItems);
      newInvoiceItems.splice(lineItemIndex, 1);

      updateInvoiceItemsAndTotalsForInvoiceComponent({
        invoiceComponentIndex,
        newInvoiceItemsForInvoiceComponent: newInvoiceItems,
      });
    },
    [
      invoiceItems,
      updateInvoiceItemsAndTotalsForInvoiceComponent,
      invoiceComponentIndex,
    ],
  );

  const totalInterestFromInvoiceItems: number = useMemo(() => {
    return commonUtils.jsRound({
      value: invoiceItems.reduce(
        (acc, invoiceItem) =>
          acc +
          (invoiceItem.accountingAccount.tAccount === 'accruedInterest'
            ? invoiceItem.amount
            : 0),
        0,
      ),
      precision: 2,
    });
  }, [invoiceItems]);

  const paymentInKindEligibleLoans: LoanRecordFragmentGQLType[] = useMemo(() => {
    // only loans in 'funded' status with maturity dates after the invoice due date are eligible for PIK
    return (
      loansSummaryData?.loans.filter(
        (loan) =>
          loan.loanStatus === 'funded' &&
          invoice.dueDate &&
          loan.maturityDate >= invoice.dueDate,
      ) ?? []
    );
  }, [loansSummaryData, invoice.dueDate]);

  const availablePaymentInKindLoans: LoanRecordFragmentGQLType[] = useMemo(() => {
    return paymentInKindEligibleLoans.filter(
      (loan) =>
        !invoiceItems.some(
          (invoiceItem) => invoiceItem.capitalizedLoanId === loan.id,
        ),
    );
  }, [paymentInKindEligibleLoans, invoiceItems]);

  const newLineItemInputRef = useRef<HTMLInputElement>(null);

  const onClickAddPaymentInKindInterestInvoiceItem = useCallback(() => {
    if (!availablePaymentInKindLoans.length) {
      return;
    }

    const newInvoiceItems = _.cloneDeep(invoiceItems);
    const interestTAccount = _.find(
      accrualsByTAccountMap,
      (account) => account.tAccount.tAccount === 'accruedInterest',
    )?.tAccount;

    if (!interestTAccount) {
      return;
    }

    const { id, tAccount, organizationId, creditFacilityId } = interestTAccount;

    // if the selected loan is in the list of available payment in kind loans, use it as the new capitalized loan
    const newCapitalizedLoanId =
      selectedLoan?.id &&
      availablePaymentInKindLoans
        .map((loan) => loan.id)
        .includes(selectedLoan.id)
        ? selectedLoan.id
        : availablePaymentInKindLoans[0].id;

    const newPaymentInKindInterestInvoiceItem: InvoiceItemInputGQLType = {
      accountingAccountId: id,
      amount: 0,
      status: 'created' as const,
      accountingAccount: {
        id,
        tAccount,
        organizationId,
        creditFacilityId,
      },
      capitalizedLoanId: newCapitalizedLoanId,
      finleyAccruedAmount: accrualsByTAccountMap[id]?.totalAccruedAmount,
    };

    newInvoiceItems.push(newPaymentInKindInterestInvoiceItem);
    updateInvoiceItemsAndTotalsForInvoiceComponent({
      invoiceComponentIndex,
      newInvoiceItemsForInvoiceComponent: newInvoiceItems,
    });

    // focus on the new line item input ref and autoselect the amount inside the input
    // need to setTimeout because the input ref technically doesn't exist yet since a new line item is being added
    setTimeout(() => {
      newLineItemInputRef.current?.focus();
      newLineItemInputRef.current?.setSelectionRange(
        0,
        newLineItemInputRef.current.value.length,
      );
    }, 100);
  }, [
    invoiceItems,
    updateInvoiceItemsAndTotalsForInvoiceComponent,
    selectedLoan,
    availablePaymentInKindLoans,
    accrualsByTAccountMap,
    invoiceComponentIndex,
  ]);

  const cashInterestInvoiceItems = useMemo(() => {
    return invoiceItems.filter(
      (invoiceItem) =>
        invoiceItem.accountingAccount.tAccount === 'accruedInterest' &&
        !invoiceItem.capitalizedLoanId,
    );
  }, [invoiceItems]);

  const onClickAddCashInterestInvoiceItem = useCallback(() => {
    if (cashInterestInvoiceItems.length) {
      // if there is already a cash interest item, don't add another one
      return;
    }

    const newInvoiceItems = _.cloneDeep(invoiceItems);
    const interestTAccount = _.find(
      accrualsByTAccountMap,
      (account) => account.tAccount.tAccount === 'accruedInterest',
    )?.tAccount;

    if (!interestTAccount) {
      return;
    }
    const { id, tAccount, organizationId, creditFacilityId } = interestTAccount;
    const newCashInterestInvoiceItem: InvoiceItemInputGQLType = {
      accountingAccountId: id,
      amount: 0,
      status: 'created' as const,
      accountingAccount: {
        id,
        tAccount,
        organizationId,
        creditFacilityId,
      },
      finleyAccruedAmount: accrualsByTAccountMap[id]?.totalAccruedAmount,
    };

    newInvoiceItems.push(newCashInterestInvoiceItem);
    updateInvoiceItemsAndTotalsForInvoiceComponent({
      invoiceComponentIndex,
      newInvoiceItemsForInvoiceComponent: newInvoiceItems,
    });

    // focus on the new line item input ref and autoselect the amount inside the input
    // need to setTimeout because the input ref technically doesn't exist yet since a new line item is being added
    setTimeout(() => {
      newLineItemInputRef.current?.focus();
      newLineItemInputRef.current?.setSelectionRange(
        0,
        newLineItemInputRef.current.value.length,
      );
    }, 100);
  }, [
    invoiceItems,
    updateInvoiceItemsAndTotalsForInvoiceComponent,
    accrualsByTAccountMap,
    cashInterestInvoiceItems.length,
    invoiceComponentIndex,
  ]);

  // @dani make better skeleton
  if (!loansSummaryData || isLoansSummaryLoading) {
    return <Skeleton />;
  }

  return (
    <Flex flexDirection="column" gap="16px">
      {/* Total Interest Amount determined based on the individual interest invoice items */}
      <Typography variant="base-muted">
        {dollarNumberWithCommas(
          commonUtils.jsRound({
            value: totalInterestFromInvoiceItems,
            precision: 2,
          }),
        )}
      </Typography>

      {/* Individual Interest Invoice Amounts for Cash and PIK */}
      {invoiceItems.map((invoiceItem, lineItemIndex) => {
        if (invoiceItem.accountingAccount.tAccount !== 'accruedInterest') {
          return null;
        }

        const key = lineItemIndex;

        return (
          <Flex key={key}>
            <Box
              borderColor={colors.gray[100]}
              borderWidth="1px"
              borderRadius="8px"
              p="24px"
              mr="24px"
              flexGrow={1}
            >
              {invoiceItem.capitalizedLoanId && paymentInKindEligibleLoans ? (
                <PaymentInKindInterestItem
                  invoiceItem={invoiceItem}
                  lineItemIndex={lineItemIndex}
                  paymentInKindEligibleLoans={paymentInKindEligibleLoans}
                  invoiceComponentIndex={invoiceComponentIndex}
                  inputRef={newLineItemInputRef}
                />
              ) : (
                <CashInterestItem
                  invoiceItem={invoiceItem}
                  lineItemIndex={lineItemIndex}
                  invoiceComponentIndex={invoiceComponentIndex}
                  inputRef={newLineItemInputRef}
                />
              )}
            </Box>
            <Button
              variant="v2-ghost"
              onClick={() => onClickDeleteInterestItem(lineItemIndex)}
            >
              <Icon icon="x" boxSize={3} />
            </Button>
          </Flex>
        );
      })}

      {!!(totalInterestFromInvoiceItems > totalAccruedInterest) && (
        <Alert status="warning">
          <AlertIcon />
          <AlertDescription fontWeight={500}>
            The combined Cash and Interest Capitalization amount exceeds{' '}
            {dollarNumberWithCommas(
              commonUtils.jsRound({
                value: totalAccruedInterest,
                precision: 2,
              }),
            )}
            .
          </AlertDescription>
        </Alert>
      )}

      <Flex>
        <Button
          variant="v2-ghost"
          isDisabled={!availablePaymentInKindLoans.length}
          onClick={onClickAddPaymentInKindInterestInvoiceItem}
          w="fit-content"
          gap="8px"
        >
          <Icon icon="plus" color={colors.gray[500]} />
          <Typography fontWeight="bold" variant="base-muted">
            Interest Capitalization
          </Typography>
        </Button>
        <Button
          variant="v2-ghost"
          isDisabled={!!cashInterestInvoiceItems.length} // if a cash interest item already exists, don't add another one
          onClick={onClickAddCashInterestInvoiceItem}
          w="fit-content"
          gap="8px"
        >
          <Icon icon="plus" color={colors.gray[500]} />
          <Typography fontWeight="bold" variant="base-muted">
            Cash
          </Typography>
        </Button>
      </Flex>
    </Flex>
  );
};

const InterestLineItemRow = ({
  totalAccruedInterest,
  accrualsByTAccountMap,
  invoiceComponentIndex,
}: {
  totalAccruedInterest: number;
  accrualsByTAccountMap: AccrualsByTAccountMap;
  invoiceComponentIndex: number;
}) => {
  const {
    selectedEntities: {
      creditFacility: { creditFacility: selectedCreditFacility },
    },
  } = useCreateInvoiceContext();

  if (!selectedCreditFacility) {
    return null;
  }

  return (
    <Tr>
      <Td color={colors.gray[500]} fontWeight={400} verticalAlign="top">
        Interest
      </Td>
      <Td color={colors.gray[500]} verticalAlign="top">
        {dollarNumberWithCommas(
          commonUtils.jsRound({
            value: totalAccruedInterest,
            precision: 2,
          }),
        )}
      </Td>
      <Td colSpan={2} pr="12px" verticalAlign="top">
        <LineItemInterestInvoiceAmountCellContent
          invoiceComponentIndex={invoiceComponentIndex}
          totalAccruedInterest={totalAccruedInterest}
          selectedCreditFacilityId={selectedCreditFacility.id}
          accrualsByTAccountMap={accrualsByTAccountMap}
        />
      </Td>
    </Tr>
  );
};

const NonInterestLineItemRow = ({
  lineItemIndex,
  accrualsByTAccountMap,
  invoiceComponentIndex,
  inputRef,
  disableInputs,
}: {
  lineItemIndex: number;
  accrualsByTAccountMap: AccrualsByTAccountMap;
  invoiceComponentIndex: number;
  inputRef: RefObject<HTMLInputElement>;
  disableInputs: boolean;
}) => {
  const {
    invoiceComponentsWithInvoiceItems,
    updateInvoiceItemsAndTotalsForInvoiceComponent,
  } = useCreateInvoiceContext();

  const invoiceItems =
    invoiceComponentsWithInvoiceItems[invoiceComponentIndex].invoiceItems;

  const onClickDeleteRow = useCallback(() => {
    const newInvoiceItems = _.cloneDeep(invoiceItems);
    newInvoiceItems.splice(lineItemIndex, 1);

    updateInvoiceItemsAndTotalsForInvoiceComponent({
      invoiceComponentIndex,
      newInvoiceItemsForInvoiceComponent: newInvoiceItems,
    });
  }, [
    invoiceItems,
    updateInvoiceItemsAndTotalsForInvoiceComponent,
    lineItemIndex,
    invoiceComponentIndex,
  ]);

  const isLastRemainingInvoiceItem = invoiceItems.length === 1;

  return (
    <Tr>
      <Td color={colors.gray[500]} fontWeight={400}>
        <LineItemOptionDropdown
          lineItemIndex={lineItemIndex}
          accrualsByTAccountMap={accrualsByTAccountMap}
          invoiceComponentIndex={invoiceComponentIndex}
        />
      </Td>
      <Td color={colors.gray[500]}>
        {dollarNumberWithCommas(
          commonUtils.jsRound({
            value:
              accrualsByTAccountMap[
                invoiceItems[lineItemIndex].accountingAccountId
              ]?.totalAccruedAmount,
            precision: 2,
          }),
        )}
      </Td>
      <Td>
        <Tooltip
          label={
            disableInputs
              ? 'This amount is locked and cannot be edited.'
              : undefined
          }
          placement="bottom-start"
        >
          <Box>
            <CurrencyInput
              textAlign="left"
              state={invoiceItems[lineItemIndex].amount}
              updateState={(newInputAmount) => {
                const newInvoiceItems = _.cloneDeep(invoiceItems);
                const currentInvoiceItem = newInvoiceItems[lineItemIndex];

                const newInvoiceItem: InvoiceItemInputGQLType = {
                  ...currentInvoiceItem,
                  amount: newInputAmount ?? 0,
                };

                newInvoiceItems.splice(lineItemIndex, 1, newInvoiceItem);

                updateInvoiceItemsAndTotalsForInvoiceComponent({
                  invoiceComponentIndex,
                  newInvoiceItemsForInvoiceComponent: newInvoiceItems,
                });
              }}
              inputRef={inputRef}
              isDisabled={disableInputs}
            />
          </Box>
        </Tooltip>
      </Td>
      <Td px="0">
        <Tooltip
          label={
            isLastRemainingInvoiceItem
              ? 'An invoice requires at least one line item'
              : undefined
          }
        >
          <Button
            variant="v2-ghost"
            onClick={onClickDeleteRow}
            isDisabled={isLastRemainingInvoiceItem}
          >
            <Icon icon="x" boxSize={3} />
          </Button>
        </Tooltip>
      </Td>
    </Tr>
  );
};

/**
 * This placeholder row is only shown if not all shares on the loan have been marked as funded
 * It contains information about the outstanding balance and a warning alert
 */
const PlaceholderOutstandingBalanceRow = ({
  totalOutstandingBalanceAmount,
  onClickDeletePlaceholderOutstandingBalanceRow,
}: {
  totalOutstandingBalanceAmount: number;
  onClickDeletePlaceholderOutstandingBalanceRow: () => void;
}) => {
  return (
    <>
      <Tr>
        <Td colSpan={3} borderBottom="none" pb="0">
          <Alert status="warning">
            <AlertIcon />
            <AlertDescription fontWeight={500}>
              All shares on the loan must be marked as funded prior to paying
              down the principal.
            </AlertDescription>
          </Alert>
        </Td>
        <Td borderBottom="none" pb="0" />
      </Tr>
      <Tr>
        <Td color={colors.gray[500]} fontWeight={400}>
          Principal
        </Td>
        <Td color={colors.gray[500]}>
          {dollarNumberWithCommas(
            commonUtils.jsRound({
              value: totalOutstandingBalanceAmount,
              precision: 2,
            }),
          )}
        </Td>
        <Td>
          <CurrencyInput
            textAlign="left"
            isDisabled
            isReadOnly
            updateState={() => {}}
          />
        </Td>
        <Td px="0">
          <Button
            variant="v2-ghost"
            onClick={onClickDeletePlaceholderOutstandingBalanceRow}
          >
            <Icon icon="x" boxSize={3} />
          </Button>
        </Td>
      </Tr>
    </>
  );
};

export const CreateInvoiceLineItemsTable = ({
  accrualsByTAccountMap,
  areAllComponentsFundedOnLoan,
  totalAccruedAndProjectedOutstandingBalanceAmount,
  invoiceComponentIndex,
  disableInputs = false,
}: {
  accrualsByTAccountMap: AccrualsByTAccountMap;
  areAllComponentsFundedOnLoan: boolean;
  totalAccruedAndProjectedOutstandingBalanceAmount: number;
  invoiceComponentIndex: number;
  disableInputs?: boolean;
}) => {
  const {
    invoiceComponentsWithInvoiceItems,
    updateInvoiceItemsAndTotalsForInvoiceComponent,
  } = useCreateInvoiceContext();

  const [
    displayUnfundedPlaceholderOutstandingBalanceRow,
    setDisplayUnfundedPlaceholderOutstandingBalanceRow,
  ] = useState(false);

  const invoiceComponent =
    invoiceComponentsWithInvoiceItems[invoiceComponentIndex];
  const invoiceItemsForInvoiceComponent = invoiceComponent.invoiceItems;

  const remainingTAccountOptions: {
    accountingAccount: TAccountGQLType;
    accrualAmount: number;
  }[] = useMemo(() => {
    const allUsedAccountingAccountIds = invoiceItemsForInvoiceComponent.map(
      (invoiceItem) => invoiceItem.accountingAccountId,
    );

    const unusedTAccounts = _.compact(
      _.map(accrualsByTAccountMap, (accountDetails) => {
        const accountingAccountId = accountDetails.tAccount.id;
        if (!allUsedAccountingAccountIds.includes(accountingAccountId)) {
          return {
            accountingAccount: accountDetails.tAccount,
            accrualAmount: accountDetails.totalAccruedAmount,
          };
        }
        return undefined;
      }),
    );

    return unusedTAccounts;
  }, [invoiceItemsForInvoiceComponent, accrualsByTAccountMap]);

  const newLineItemInputRef = useRef<HTMLInputElement>(null);

  const onClickAddRow = useCallback(() => {
    if (remainingTAccountOptions.length) {
      const newInvoiceItems = _.cloneDeep(invoiceItemsForInvoiceComponent);

      const {
        id,
        tAccount,
        organizationId,
        creditFacilityId,
      } = remainingTAccountOptions[0].accountingAccount;

      // we want to default long term debt to 0 so the user can fill it in on their own
      const newItemAmount =
        tAccount === 'longTermDebt'
          ? 0
          : commonUtils.jsRound({
              value: remainingTAccountOptions[0].accrualAmount,
              precision: 2,
            });

      // no need to attach finley accrued amount for principal
      // only applicable to tAccount types that actually accrue
      const newInvoiceItemFinleyAccruedAmount =
        tAccount === 'longTermDebt' ? null : newItemAmount;

      const newInvoiceItem = {
        accountingAccountId: id,
        accountingAccount: {
          id,
          tAccount,
          organizationId,
          creditFacilityId,
        },
        amount: newItemAmount,
        finleyAccruedAmount: newInvoiceItemFinleyAccruedAmount,
        status: 'created' as const,
      };

      newInvoiceItems.push(newInvoiceItem);

      updateInvoiceItemsAndTotalsForInvoiceComponent({
        invoiceComponentIndex,
        newInvoiceItemsForInvoiceComponent: newInvoiceItems,
      });

      // focus on the new line item input ref and autoselect the amount inside the input
      // need to setTimeout because the input ref technically doesn't exist yet since a new line item is being added
      setTimeout(() => {
        newLineItemInputRef.current?.focus();
        newLineItemInputRef.current?.setSelectionRange(
          0,
          newLineItemInputRef.current.value.length,
        );
      }, 100);
    }

    if (
      !remainingTAccountOptions.length &&
      !areAllComponentsFundedOnLoan &&
      invoiceComponent.invoiceComponent.invoiceComponentType === 'loan'
    ) {
      setDisplayUnfundedPlaceholderOutstandingBalanceRow(true);
    }
  }, [
    invoiceItemsForInvoiceComponent,
    remainingTAccountOptions,
    invoiceComponentIndex,
    updateInvoiceItemsAndTotalsForInvoiceComponent,
    areAllComponentsFundedOnLoan,
    invoiceComponent.invoiceComponent.invoiceComponentType,
  ]);

  const interestInvoiceItems = invoiceItemsForInvoiceComponent.filter(
    (invoiceItem) =>
      invoiceItem.accountingAccount.tAccount === 'accruedInterest',
  );
  const totalAccruedInterest = commonUtils.jsRound({
    value: _.sumBy(
      _.filter(
        accrualsByTAccountMap,
        (mappedAccount) =>
          mappedAccount.tAccount.tAccount === 'accruedInterest',
      ),
      (tAccount) => tAccount.totalAccruedAmount,
    ),
    precision: 2,
  });

  // we allow adding a line item if there are remaining tAccounts to add
  // or if we are adding the placeholder outstanding balance alert row when not all components are funded
  const isAddLineItemButtonEnabled =
    !!remainingTAccountOptions.length ||
    (!areAllComponentsFundedOnLoan &&
      !displayUnfundedPlaceholderOutstandingBalanceRow);

  return (
    <>
      <Flex justifyContent="flex-end" mb="24px">
        <Button
          variant="unstyled"
          isDisabled={!isAddLineItemButtonEnabled}
          onClick={onClickAddRow}
        >
          <Flex
            alignItems="center"
            _hover={{
              textDecoration: remainingTAccountOptions.length
                ? 'underline'
                : undefined,
            }}
          >
            <Icon icon="plus" color={colors.gray[500]} />
            <Text ml="8px" fontWeight={500} color={colors.gray[500]}>
              Add Line Item
            </Text>
          </Flex>
        </Button>
      </Flex>
      <Box overflowY="auto" width="100%">
        <Table borderWidth="1px" borderColor={colors.gray[100]}>
          <Thead>
            <Tr>
              <Th w="20%" fontWeight={600} fontSize="14px">
                Item
              </Th>
              <Th w="20%" fontWeight={600} fontSize="14px">
                Outstanding Amount
              </Th>
              <Th w="56%" fontWeight={600} fontSize="14px">
                Amount To Pay
              </Th>
              <Th w="4%" />
            </Tr>
          </Thead>
          <Tbody>
            {/* Interest Line Item Row */}
            {interestInvoiceItems.length > 0 && (
              <InterestLineItemRow
                totalAccruedInterest={totalAccruedInterest}
                accrualsByTAccountMap={accrualsByTAccountMap}
                invoiceComponentIndex={invoiceComponentIndex}
              />
            )}

            {/* Non-Interest Line Item Rows */}
            {invoiceItemsForInvoiceComponent.map((invoiceItem, index) => {
              if (
                invoiceItem.accountingAccount.tAccount === 'accruedInterest'
              ) {
                return null;
              }

              const key = index;
              return (
                <NonInterestLineItemRow
                  key={`${invoiceItem.accountingAccount.tAccount}-${key}`}
                  lineItemIndex={index}
                  accrualsByTAccountMap={accrualsByTAccountMap}
                  invoiceComponentIndex={invoiceComponentIndex}
                  inputRef={newLineItemInputRef}
                  disableInputs={disableInputs}
                />
              );
            })}

            {displayUnfundedPlaceholderOutstandingBalanceRow && (
              <PlaceholderOutstandingBalanceRow
                totalOutstandingBalanceAmount={
                  totalAccruedAndProjectedOutstandingBalanceAmount
                }
                onClickDeletePlaceholderOutstandingBalanceRow={() =>
                  setDisplayUnfundedPlaceholderOutstandingBalanceRow(false)
                }
              />
            )}

            <Tr>
              <Td colSpan={1} fontWeight={600}>
                Total
              </Td>
              <Td />
              <Td colSpan={2} fontWeight={600}>
                {dollarNumberWithCommas(
                  commonUtils.jsRound({
                    value: _.sumBy(
                      invoiceItemsForInvoiceComponent,
                      (invoiceItem) => invoiceItem.amount,
                    ),
                    precision: 2,
                  }),
                )}
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
    </>
  );
};
